<template>
    <div>
        <vue-headful title="Corrective Action Settings :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-cogs"></i> Corrective Action Settings</li>
            </ol>
        </h4>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <div class="row">
                <div class="col-xl-9 col-lg-10 col-md-12 mb-3">
                    <b-card class="mb-3">
                        <strong slot="header">
                            Statuses
                            <button type="button" class="btn btn-sm btn-info" v-on:click="createStatus()"><i class="fa fa-plus-circle"></i> Add</button>
                        </strong>
                        <template v-if="newStatus != null">
                            <div class="form-inline mb-3" >
                                <b-form-input v-model="newStatus.status_name" placeholder="New Status Name" :class="{'mr-2': true, 'is-invalid': errors.status_name}"></b-form-input>
                                <b-form-select v-model="newStatus.status_context" :options="options" :class="{'mr-2': true, 'is-invalid': errors.status_context}"></b-form-select>
                                <b-form-select v-model="newStatus.status_color" :options="colors" :class="{'mr-2': true, 'is-invalid': errors.status_color}"></b-form-select>
                            </div>
                            <div class="mt-2">
                                <b-button variant="success" size="sm" v-on:click="saveStatus()" class="mr-1">Save</b-button>
                                <b-button variant="light" size="sm" v-on:click="newStatus = null">Cancel</b-button>
                            </div>
                            <hr />
                            <div class="alert alert-danger" v-if="errors.status_name || errors.status_context || errors.status_color">
                                <ul class="text-danger">
                                    <li v-if="errors.status_name">{{ errors.status_name[0] }}</li>
                                    <li v-if="errors.status_context">{{ errors.status_context[0] }}</li>
                                    <li v-if="errors.status_color">{{ errors.status_color[0] }}</li>
                                </ul>
                            </div>
                        </template>
                        <b-table striped bordered hover condensed :items="statuses" :fields="statusFields">
                            <template v-slot:cell(status_id)="data">
                                <b-button variant="link" size="xs" v-on:click="editStatus(data.item)"><i class="fa fa-edit"></i> Edit</b-button>
                            </template>
                            <template v-slot:cell(status_color)="data">
                                <label :class="`badge badge-${data.item.status_color}`">{{ data.item.status_color }}</label>
                            </template>
                        </b-table>
                    </b-card>

                    <b-card class="mb-3">
                        <strong slot="header">
                            Sources
                            <button type="button" class="btn btn-sm btn-info" v-on:click="createSource()"><i class="fa fa-plus-circle"></i> Add</button>
                        </strong>
                        <div class="form-inline mb-3" v-if="newSource != null">
                            <b-form-input v-model="newSource.source_name" placeholder="New Source Name" class="mr-2"></b-form-input>
                            <b-button variant="success" v-on:click="saveSource()" class="mr-2" :disabled="saving">Save</b-button>
                            <b-button variant="light" v-on:click="newSource = null" :disabled="saving">Cancel</b-button>
                            <hr/>
                        </div>
                        <b-table striped bordered hover condensed :items="sources" :fields="sourceFields">
                            <template v-slot:cell(corrective_source_id)="data">
                                <b-button variant="link" size="xs" v-on:click="editSource(data.item)"><i class="fa fa-edit"></i> Edit</b-button>
                            </template>
                        </b-table>
                    </b-card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                newStatus: null,
                newSource: null,
                contexts: [],
                statuses: [],
                sources: [],
                enable_action_reminders: 0,
                errors: {},

                colors: [
                    {value: null, text: '-- Select a Color --'},
                    {value: 'success', text: 'Green'},
                    {value: 'warning', text: 'Yellow'},
                    {value: 'danger', text: 'Red'},
                    {value: 'info', text: 'Blue'},
                    {value: 'dark', text: 'Dark'},
                    {value: 'light', text: 'Light Gray'},
                ],

                statusFields: [
                    {key: 'status_id', label: 'Actions'},
                    {key: 'status_name', label: 'Name'},
                    {key: 'status_context', label: 'Context'},
                    {key: 'status_color', label: 'Color'},
                ],
                sourceFields: [
                    {key: 'corrective_source_id', label: 'Actions'},
                    {key: 'source_name', label: 'Name'},
                ]
            }
        },

        computed: {
            options(){
                //Build it
                var options = [
                    {value: null, text: '-- Select a Context --'},
                ];

                for(var opt in this.contexts)
                {
                    options.push({value: this.contexts[opt], text: this.contexts[opt]});
                }

                //Send it
                return options;
            }
        },

        methods: {
            //Get the existing sources
            getSettings(){
                //Get the data
                this.$http
                    .get('admin/corrective-actions')
                    .then(response => {
                        //Set all the data
                        this.contexts = response.data.contexts;
                        this.statuses = response.data.statuses;
                        this.sources = response.data.sources;
                    })
                    .catch(err => {
                        //Alert
                        this.$toastr.error(err.response.data);
                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            createStatus(){
                //Set to an empty object
                this.newStatus = {
                    status_color: null,
                    status_context: null,
                };
            },

            editStatus(status){
                //Set it
                this.newStatus = status;
            },

            saveStatus(){
                //Helper
                this.saving = true;

                //Reset Errors
                this.errors = {};

                //Send it
                this.$http
                .post('admin/corrective-actions/status', this.newStatus)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Status Saved');

                    //Reset
                    this.newStatus = null;

                    //Set it
                    this.updateStatuses(response.data);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                    else
                    {
                        //Alert the user
                        this.$toastr.error(error.response.data, 'Failed to Save Status');
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            updateStatuses(status, deleteMode = false){
                //Loop and set
                for (var i = this.statuses.length - 1; i >= 0; i--)
                {
                    //If the IDs match, replace it
                    if(this.statuses[i].status_id == status.status_id)
                    {
                        //Splice and reset
                        if(deleteMode)
                        {
                            //SPlice only
                            this.statuses.splice(i, 1);
                        }
                        else
                        {
                            //Splice and replace so reactivity is preserved
                            this.statuses.splice(i, 1, status);
                        }

                        //Stop here;
                        return;
                    }
                }

                //If it get here, it's a new bid. Push it
                this.statuses.push(status);
            },

            createSource(){
                //Set it
                this.newSource = {};
            },

            editSource(source){
                //SEt it
                this.newSource = source;
            },

            saveSource(){
                //Helper
                this.saving = true;

                //Send it
                this.$http
                .post('admin/corrective-actions/source', this.newSource)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Source Saved');

                    //Reset
                    this.newSource = null;

                    //Set it
                    this.sources = response.data;
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                    else
                    {
                        //Alert the user
                        this.$toastr.error(error.response.data, 'Failed to Save Source');
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },
        },

        mounted(){
            //Get the sources
            this.getSettings();
        }
    }
</script>
